export type CorNomeada =
  'azul' | 'verde' | 'amarelo' | 'dourado' | 'laranja' | 'vermelho' | 'preto' | 'cinza' | 'roxo';

export interface BrandColor {
  main: string;
  dark: string;
  light: string;
}

// `dark` e `light` são declarados em vez de derivados de `main`: o cálculo automático
// do MUI produz tons que não coincidem com --default-color-dark e
// --default-color-accent do SCSS legado, e as duas folhas de estilo coexistem na
// mesma tela.
export const BRAND_COLORS: Readonly<Record<CorNomeada, BrandColor>> = {
  azul: { main: '#0288d1', dark: '#015f92', light: '#4eacdf' },
  verde: { main: '#3e7d43', dark: '#2b582f', light: '#78a47b' },
  amarelo: { main: '#f9b000', dark: '#c78d00', light: '#fac033' },
  dourado: { main: '#ae7a1c', dark: '#7a5514', light: '#c6a260' },
  laranja: { main: '#ff6800', dark: '#cc5300', light: '#ff8633' },
  vermelho: { main: '#ab1101', dark: '#780c01', light: '#c4584d' },
  preto: { main: '#2f2f2f', dark: '#262626', light: '#595959' },
  cinza: { main: '#6a6b6c', dark: '#4a4b4c', light: '#979798' },
  roxo: { main: '#66069e', dark: '#4d0578', light: '#9323c8' },
};

// `azul` espelha o bgColor default do FullLayout: cor ausente ou desconhecida cai
// na mesma entrada, nunca em "sem cor".
export const COR_DEFAULT: CorNomeada = 'azul';

// Divergem de propósito das variáveis de cor da folha legada (--danger-color,
// --warning-color, --success-color): os tons de lá não atingem contraste WCAG AA
// sobre fundo claro, e estes papéis só existem no lado React.
export const SEMANTIC_ROLES = {
  secondary: '#546E7A',
  error: '#C62828',
  warning: '#E65100',
  info: '#0277BD',
  success: '#2E7D32',
} as const;

export const FONT_FAMILY = '"Roboto", "Montserrat", sans-serif';
export const FONT_SIZE_BASE = 14;

// A escala de títulos do legado ($h1-font-size a $h6-font-size, 40/32/28/24/20/16
// sobre $font-size-root de 16px) não é misturável com os tamanhos congelados de h4,
// h5 e h6: o h4 do módulo (34) fica acima do h3 do legado (28), então adotar as duas
// famílias inverteria a ordem entre h3 e h4. h1, h2 e h3 seguem a mesma família de
// h4/h5/h6 (Material Design) — que é também o que essas variantes já renderizam hoje
// por default do MUI.
export const FONT_SIZES = {
  h1: 96,
  h2: 60,
  h3: 48,
  h4: 34,
  h5: 24,
  h6: 20,
  subtitle1: 16,
  subtitle2: 14,
  body1: 16,
  body2: 14,
  button: 14,
  caption: 12,
  overline: 12,
} as const;

// Paridade com $grid-breakpoints do Bootstrap 4 usado pelo legado: um layout escrito
// em MUI e um escrito com media-breakpoint-up() quebram na mesma largura de viewport.
// Os cinco valores substituem a escala default do MUI (600/900/1200/1536), não a
// complementam.
export const BREAKPOINTS = { xs: 0, sm: 576, md: 768, lg: 992, xl: 1200 } as const;

// Paridade com $border-radius-base: um card MUI e um card legado na mesma tela
// precisam do mesmo arredondamento.
export const BORDER_RADIUS = 4;
export const SPACING_UNIT = 8;

// Forma oficial do botão: raio de pílula, tipografia Montserrat, e altura/padding do
// tamanho `medium`, o único tamanho declarado — `small`/`large` seguem a escala
// default do MUI. Cor fica de fora de propósito: o verde do botão de salvar do layout
// DSS é contextual a esse layout e à ação "salvar", não uma cor de base de todo botão
// preenchido — quem precisar dele usa `color="success"`, já mapeado em
// `SEMANTIC_ROLES`. Vale para todo `contained`/`outlined`/`text` do app via
// `components.MuiButton`, não é ajuste de instância.
export const BUTTON_SHAPE = {
  borderRadius: 50,
  height: 40,
  paddingX: 24,
  fontFamily: FONT_FAMILY,
  fontWeight: 600,
  lineHeight: '150%',
} as const;

// Paridade com a tabela do legado: zebrado/hover (nth-child(even) e :hover em
// rgba(0,0,0,.08)/.12 na folha de estilo global do legado — coeficientes próprios,
// não action.selected/hover do tema, para não colidir com o destaque de linha
// selecionada), padding de célula (convergência de três fontes legadas no mesmo par
// vertical/horizontal, bem mais compacto que o padding default do MUI) e peso do
// cabeçalho (mixin de título de tabela do legado). Vale para toda `<Table>` do app
// via `components.MuiTableCell`/`MuiTableBody`, não é ajuste de instância.
export const TABLE_SHAPE = {
  cellPadding: '0.35rem 0.55rem',
  headerFontWeight: 600,
  stripedRowAlpha: 0.08,
  hoverRowAlpha: 0.25,
} as const;

export const COMMON = { white: '#ffffff', black: '#000000' } as const;

// Escala neutra do Material Design, declarada em vez de herdada: assim um upgrade do
// MUI não desloca em silêncio os componentes que leem grey[300]/grey[700] (Chip,
// Tooltip). Os tons quentes do legado ficam em NEUTRAL_GREYS e de propósito não
// entram aqui — ocupar os slots 300/700 com eles mudaria a aparência desses
// componentes.
export const GREY_SCALE = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#eeeeee',
  300: '#e0e0e0',
  400: '#bdbdbd',
  500: '#9e9e9e',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
} as const;

// Tons de cinza do legado ($background-light-grey, $background-lighter-grey,
// $font-background-light-grey, $font-hover-background-light-grey), para consumo
// direto por quem precisar do cinza quente das telas Angular.
export const NEUTRAL_GREYS = {
  backgroundLight: '#E8E7E3',
  backgroundLighter: '#F0EFEB',
  fontOverLight: '#9C9B99',
  fontOverLightHover: '#5E5E5C',
} as const;

// $font-background-light-grey (#9C9B99) é o tom de texto do legado sobre fundo claro,
// mas rende 2.59:1 sobre #f5f7fa — abaixo do mínimo de 4.5:1 (WCAG AA) para texto. O
// substituto é o tom de hover do próprio legado, $font-hover-background-light-grey,
// que rende 6.06:1. Use este, não aquele, para texto.
export const TEXT_LEGACY_SUBSTITUTE = NEUTRAL_GREYS.fontOverLightHover;

// `default` tem paridade com $body-bg; `paper` é a superfície de card do legado.
export const BACKGROUND = { default: '#f5f7fa', paper: '#ffffff' } as const;

// Coeficientes sobre preto, não hexadecimais compostos: o tom de texto precisa
// acompanhar o fundo sobre o qual assenta, e o mesmo coeficiente serve tanto sobre
// `default` quanto sobre `paper`. É a mesma construção que o corpo do legado já usa.
export const TEXT_ALPHA = { primary: 0.87, secondary: 0.6, disabled: 0.38 } as const;

// O legado não tem variável de cor de borda — deriva por darken($body-bg, …). Copiar
// o resultado computado deixaria o valor preso ao $body-bg de hoje, sem acompanhá-lo
// se ele mudar; o coeficiente sobre preto usa a mesma construção do texto acima.
export const DIVIDER_ALPHA = 0.12;

// `disabled` é a cor de um elemento inativo; `disabledOpacity` é o coeficiente que o
// MUI aplica a outro elemento no estado inativo. São valores distintos apesar dos
// nomes próximos. Cor e coeficiente de cada estado saem do mesmo número aqui: o MUI
// não recalcula um a partir do outro, e declarar só um deixaria os dois divergindo.
export const ACTION_ALPHA = {
  active: 0.54,
  hover: 0.04,
  selected: 0.08,
  disabled: 0.26,
  disabledBackground: 0.12,
  disabledOpacity: 0.38,
  focus: 0.12,
  activatedOpacity: 0.12,
} as const;

// Os overlays da sidebar são branco translúcido: o branco não varia com a cor da
// conta, só o coeficiente de opacidade distingue cada estado.
export const OVERLAY_WHITE = '#ffffff';
export const SIDEBAR_OVERLAY_ALPHA = {
  hover: 0.08,
  selected: 0.16,
  selectedHover: 0.24,
  border: 0.2,
  skeleton: 0.2,
} as const;
